import globalLess from '@/global.less'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useGridApiContext } from '@mui/x-data-grid'
import { DataGrid } from '@mui/x-data-grid/DataGrid'
import throttle from 'lodash/throttle'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Pagination from './Pagination'

const SortIcon = ({ sort }) => (sort === 'asc' ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />)

const Header = ({ headerName, field, onMouseDown, onMouseUp }) => {
  const apiRef = useGridApiContext()
  const sortModel = apiRef.current.getSortModel()
  const { sort } = sortModel.find(model => model.field === field) || {}

  return (
    <Stack direction="row" alignItems="center" width="100%">
      <Button
        color="inherit"
        sx={{ '&:hover': { background: 'transparent' }, minWidth: 0 }}
        onClick={() => {
          apiRef.current?.setSortModel?.([{ field, sort: sort === 'desc' ? 'asc' : 'desc' }])
        }}
      >
        {headerName ?? field}
      </Button>
      {sort && <SortIcon sort={sort} />}
      <Box flex={1} />
      <Stack
        sx={{ height: 30, minWidth: 10, alignItems: 'center', cursor: 'col-resize' }}
        onMouseUp={onMouseUp}
        onMouseDown={e => {
          onMouseDown(e, field)
        }}
      >
        <Divider
          sx={{ bgcolor: theme => theme.palette.white.main, opacity: 0.4 }}
          orientation="vertical"
        />
      </Stack>
    </Stack>
  )
}

const calcMinWidth = ({ field, headerName }) => {
  const name = headerName ?? field

  return name.length * 13 + 68
}

// const calcMinTimeWidth = ({ field, headerName }) => {
//   const name = headerName ?? field

//   return name.length * 13 + 78
// }

const generateColWidths = cols =>
  cols.map(({ field, headerName, width }) => ({
    field,
    width: width || calcMinWidth({ field, headerName }),
  }))

const generateColMinWidths = cols =>
  cols.map(({ field, headerName }) => ({
    field,
    width: calcMinWidth({ field, headerName }),
  }))

const Table = ({
  // minHeight = 350,
  width = '100%',
  rows,
  columns,
  dataGridConfig = {},
  noRowsLabel,
  pagination = {},
  containerStyle = {},
}) => {
  const { t } = useTranslation()
  const minWidths = useRef(generateColMinWidths(columns))
  const [columnWidths, setColumnWidths] = useState(generateColWidths(columns))
  // const [columnWidthsTotal, setColumnWidthsTotal] = useState(1)
  const [gridWidth, setGridWidth] = useState()
  const { sx = {}, borderColor, components, ..._dataGridConfig } = dataGridConfig
  const { totalPages, page, pageSize, onPageChange, onPageSizeChange, ...paginationProps } = pagination
  const mouseOrigin = useRef()
  const initialWidth = useRef()
  const resizeField = useRef()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttleWidth = useCallback(
    throttle(newWidth => {
      const { width: colMinWidth = 0 } =
        minWidths.current?.find(col => col.field === resizeField.current) || {}

      if (newWidth > colMinWidth) {
        setColumnWidths(oldWidths => {
          const newColWidths = [...oldWidths]

          const item = newColWidths.find(col => col.field === resizeField.current)

          if (item) {
            item.width = newWidth
          }
          return newColWidths
        })
      }
    }, 1),
    [],
  )

  const onMouseMove = e => {
    e.preventDefault()

    const diffX = e.clientX - (mouseOrigin.current?.x || 0)
    const newWidth = initialWidth.current + diffX

    throttleWidth(newWidth)
  }

  const onMouseUp = () => {
    document.removeEventListener('pointerup', onMouseUp)
    document.removeEventListener('pointermove', onMouseMove)
  }

  const onMouseDown = (e, field) => {
    const { width: colWidth = 0 } = columnWidths.find(col => col.field === field) || {}
    document.addEventListener('pointerup', onMouseUp)
    document.addEventListener('pointermove', onMouseMove)

    mouseOrigin.current = { x: e.clientX, y: e.clientY }
    initialWidth.current = colWidth
    resizeField.current = field
  }

  // useEffect(() => {
  //   let total = 0
  //   if(columns !== null){
  //     let i = 0
  //     for(;i< columns[i];){
  //       total += columns[i].width
  //       i += 1
  //     }
  //   }
  //   setColumnWidthsTotal(total === 0 ? 1 : total)
  // }, [columns])

  useEffect(() => {
    if (gridWidth && columnWidths && columns) {
      const widthSum = columns.reduce((acc, col) => {
        const { width: colWidth } = col
        return acc + colWidth
      }, 0)

      if (widthSum < gridWidth) {
        const fixedWidth = (gridWidth - 30) / columnWidths.length
        const newWidths = columnWidths.map(col => ({ ...col, width: fixedWidth }))
        setColumnWidths(newWidths)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridWidth])

  return (
    <div
      className={globalLess.primaryScrollbar}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width,
        height: 600,
        flexGrow: 1,
        ...containerStyle,
      }}
    >
      <DataGrid
        autoHeight={false}
        rows={rows}
        onResize={size => {
          if (!gridWidth) {
            const { width: resizeWidth } = size
            setGridWidth(resizeWidth)
          }
        }}
        columns={columns
          .map(col => {
            const { width: colWidth = 0 } =
              columnWidths.find(({ field }) => field === col.field) || {}

            return {
              ...col,
              // width: col.width / (gridWidth / (columnWidthsTotal === 1 ? 1 : columnWidthsTotal) /100),
              width: col.width ?? colWidth,
              minWidth: colWidth,
              sortable: false,
              // flex: 1,
              renderHeader: ({ colDef }) => (
                <Header
                  {...colDef}
                  onMouseDown={onMouseDown}
                  onMouseUp={onMouseUp}
                  onMouseMove={onMouseMove}
                />
              ),
            }
          })
          .filter(col => !col.hidden)}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableSelectionOnClick
        sx={{
          '& .': {},

          '& .MuiDataGrid-row:nth-of-type(even)': { background: '#2D2D6C1A' },

          '& .MuiDataGrid-columnHeaders': {
            borderRadius: 0,
            background: theme => theme.palette.secondary[80].main,
            color: 'white !important',
          },

          '&.MuiDataGrid-root': {
            borderRadius: 0,
            borderWidth: '0px 0px 0px 0px',
            ...(borderColor ? { borderColor } : {}),
            color: theme => theme.palette.secondary[80].main,
          },

          '& .MuiDataGrid-columnSeparator': {
            color: 'transparent',
            display: 'none',
          },

          '& .MuiDataGrid-columnHeaderTitleContainerContent': {
            width: '100%',
          },

          '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
            ...(borderColor ? { borderColor } : {}),
          },

          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader':
          {
            outline: 'none !important',
          },

          '& .MuiDataGrid-footerContainer': { justifyContent: 'flex-end', border: 'none', mb: -3, mr: -12 },
          ...sx,
        }}
        localeText={{
          footerRowSelected: count => (
            <Typography whiteSpace="nowrap" variant="caption">
              {t('table.selected', { num: count })}
            </Typography>
          ),
          MuiTablePagination: {
            labelRowsPerPage: t('table.rows_per_page'),
            labelDisplayedRows: ({ from, to, count }) => `${from}-${to} / ${count}`,
          },
        }}
        {..._dataGridConfig}
        components={{
          ColumnSortedAscendingIcon: ArrowDropUpIcon,
          ColumnSortedDescendingIcon: ArrowDropDownIcon,
          Pagination: () => (
            <Pagination
              pageCount={totalPages}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              page={page}
              pageSize={pageSize}
              {...paginationProps}
            />
          ),
          NoRowsOverlay: () => (
            <Box
              sx={{
                display: 'flex',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6">{t(noRowsLabel || 'errors.no_data')}</Typography>
            </Box>
          ),
          ...components,
        }}
      />
    </div>
  )
}

export default Table
